import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, mergeMap, tap} from 'rxjs/operators';
import { HttpClient, HttpContext, HttpHeaders, HttpResponse } from "@angular/common/http";
import {AuthApiModel} from "../models/auth.api.model";
import {CredentialsApiModel} from "../models/credentials.api.model";
import {MessageApiModel} from "../models/message.api.model";
import {UserApiModel} from "../models/user.api-model";
import {environment} from "../../../environments/environment";
import {plainToClass} from "class-transformer";
import {FacebookLoginResponse} from "@capgo/capacitor-social-login";
import {GoogleLoginResult} from "../../core/services/google.service";
import {TokenApiModel} from "../models/token.api.model";
import {SKIP_CANCEL_ON_NAV} from "../../core/providers/http-context-tokens";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationRestService {
  constructor(
    private http: HttpClient,
  ) {}

  public login(credentials: CredentialsApiModel): Observable<AuthApiModel> {
    return this.http.post<AuthApiModel>(environment.apiUrl + '/users/auth', credentials)
      .pipe(map((successResponse) => plainToClass(AuthApiModel, successResponse)));
  }

  public loginFromInvitation(credentials: TokenApiModel): Observable<AuthApiModel> {
    return this.http.post<AuthApiModel>(environment.apiUrl + '/users/owner_invitations/accept', credentials)
      .pipe(map((successResponse) => plainToClass(AuthApiModel, successResponse)));
  }

  public loginFromOwnerChangeInvitation(credentials: TokenApiModel): Observable<AuthApiModel> {
    return this.http.post<AuthApiModel>(environment.apiUrl + '/users/main-owner-change-invitation', credentials)
      .pipe(map((successResponse) => plainToClass(AuthApiModel, successResponse)));
  }

  public denyOwnerInvitation(credentials: TokenApiModel): Observable<MessageApiModel> {
    return this.http.post<MessageApiModel>(environment.apiUrl + '/users/owner_invitations/deny', credentials);
  }

  public denyOwnerChangeInvitation(credentials: TokenApiModel): Observable<MessageApiModel> {
    return this.http.post<MessageApiModel>(environment.apiUrl + '/users/main-owner-change-invitation/deny', credentials);
  }

  public fbLogin(body: {limitedLogin: boolean, payload: FacebookLoginResponse}): Observable<AuthApiModel> {
    return this.http.post<AuthApiModel>(environment.apiUrl + '/connect/facebook/check', body)
      .pipe(map((successResponse) => plainToClass(AuthApiModel, successResponse)));
  }

  public googleLogin(body: {limitedLogin: boolean, payload: GoogleLoginResult}): Observable<AuthApiModel> {
    return this.http.post<AuthApiModel>(environment.apiUrl + '/connect/google/check', body)
      .pipe(map((successResponse) => plainToClass(AuthApiModel, successResponse)));
  }

  public getCurrentUser(fresh = false): Observable<UserApiModel> {
    const context = new HttpContext().set(SKIP_CANCEL_ON_NAV, true);
    // Cache-Control is not allowed by the API's CORS policy; use the existing
    // bypass header for requests that must always read the current user.
    const headers = fresh ? new HttpHeaders({'x-cache-bypass': 'true'}) : undefined;
    return this.http.get(environment.apiUrl + '/users/current', {context, headers, transferCache: false})
      .pipe(map((successResponse) => plainToClass(UserApiModel, successResponse)));
  }

  public resetPassword(usernameOrEmail: string): Observable<MessageApiModel> {
    const body = JSON.stringify({usernameOrEmail: usernameOrEmail});
    return this.http.post<MessageApiModel>(environment.apiUrl + '/users/password-reset', body);
  }

  public resetConfirm(token: string): Observable<MessageApiModel> {
    const body = JSON.stringify({token: token});
    return this.http.post<MessageApiModel>(environment.apiUrl + '/users/password-reset-confirm', body);
  }

  public postRefreshToken(refreshToken: string): Observable<AuthApiModel> {
    return this.http.post<AuthApiModel>(environment.apiUrl + '/token/refresh', {refreshToken})
      .pipe(map((successResponse) => plainToClass(AuthApiModel, successResponse)));
  }
}
